/**
 * Mappers — AIMS backend ↔ frontend Redux types.
 *
 * Backend uses snake_case + a slightly different field set from the
 * frontend Redux mock store. These helpers narrow the mapping to the
 * fields actually used by pages today.
 */

import { AnalysisType, ContractType } from "./enums"
import { AssetCreate, AssetRead, Contract as BackendContract, ContractCreate, PatchedContract } from "./types"

export type FrontendAsset = {
  id: number
  assetId: string
  country: string
  fla: string
  admin1: string
  admin2: string
  community: string
  analysis: "LIA" | "ADA" | "ADA/LIA"
  latY: number
  longX: number
  geotrace: string
  assetType: string
  assetSubcat: string
  assetDetail: string
  prjSize: number
  outputUnit: string
  startMonth: string
  startYear: number
  endMonth: string
  endYear: number
  villages: string
  outcomes: string
  impactArea: string
  extraInfo: string
  submissionStatus: "Complete" | "Incomplete"
}

export type ContractFormValues = {
  name: string
  owner: string
  countries: string[]
  contractTypes: ContractType[]
  subscriptionYear: number
  description?: string
  assets?: number[]
}

export function formValuesToContractCreate(values: ContractFormValues): ContractCreate {
  return {
    name: values.name,
    owner: values.owner,
    countries: values.countries,
    contract_types: values.contractTypes,
    subscription_year: values.subscriptionYear,
    description: values.description ?? "",
    assets: values.assets ?? [],
  }
}

export function formValuesToPatchedContract(values: Partial<ContractFormValues>): PatchedContract {
  const out: PatchedContract = {}
  if (values.name !== undefined) out.name = values.name
  if (values.owner !== undefined) out.owner = values.owner
  if (values.countries !== undefined) out.countries = values.countries
  if (values.contractTypes !== undefined) out.contract_types = values.contractTypes
  if (values.subscriptionYear !== undefined) out.subscription_year = values.subscriptionYear
  if (values.description !== undefined) out.description = values.description
  if (values.assets !== undefined) out.assets = values.assets
  return out
}

export function contractToFormValues(contract: BackendContract): ContractFormValues {
  return {
    name: contract.name,
    owner: contract.owner,
    countries: contract.countries,
    contractTypes: contract.contract_types,
    subscriptionYear: contract.subscription_year,
    description: contract.description ?? "",
    assets: contract.assets,
  }
}

// ────────────────────────────────────────────────────────────────────
// Assets
// ────────────────────────────────────────────────────────────────────

/**
 * Backend `AssetRead` (PascalCase) → frontend `Asset` (camelCase).
 * `submissionStatus` is frontend-only — defaults to "Complete" since
 * the API only ever returns successfully persisted rows.
 */
export function backendAssetToFrontend(a: AssetRead): FrontendAsset {
  return {
    id: a.id,
    assetId: a.AssetId ?? "",
    country: a.ISO3 ?? "",
    fla: a.FLA ?? "",
    admin1: a.Admin1 ?? "",
    admin2: a.Admin2 ?? "",
    community: a.Community ?? "",
    analysis: (a.AnalysisType as FrontendAsset["analysis"]) ?? "ADA",
    latY: a.Lat_Y,
    longX: a.Long_X,
    geotrace: a.Geotrace ?? "",
    assetType: a.AssetType ?? "",
    assetSubcat: a.AssetSubcat ?? "",
    assetDetail: a.AssetDetail ?? "",
    prjSize: a.Prj_Size ?? 0,
    outputUnit: a.OutputUnit ?? "",
    startMonth: a.StartMonth ?? "",
    startYear: a.StartYear ?? 0,
    endMonth: a.EndMonth ?? "",
    endYear: a.EndYear ?? 0,
    villages: a.Villages ?? "",
    outcomes: a.Outcomes ?? "",
    impactArea: a.ImpactArea ?? "",
    extraInfo: a.Extra_Info ?? "",
    submissionStatus: "Complete",
  }
}

/**
 * Form values used by AssetFormPage. Mirrors `Asset` minus the
 * server-set fields (id, submissionStatus).
 */
export type AssetFormValues = {
  country?: string
  fla?: string
  admin1: string
  admin2: string
  community?: string
  analysis: AnalysisType
  latY: number
  longX: number
  assetType: string
  assetSubcat: string
  assetDetail: string
  prjSize?: number
  outputUnit?: string
  startMonth: string
  startYear: number | string
  endMonth: string
  endYear: number | string
  villages?: string
  outcomes: string
  impactArea?: string
  extraInfo?: string
}

/**
 * Frontend form values → backend `AssetCreate`.
 * Note: StartYear/EndYear are sent as strings on create per the
 * documented contract — the backend coerces them to integers.
 */
export function formValuesToAssetCreate(v: AssetFormValues): AssetCreate {
  return {
    Country: v.country || undefined,
    FLA: v.fla || undefined,
    Admin1: v.admin1,
    Admin2: v.admin2,
    Community: v.community || undefined,
    Lat_Y: Number(v.latY),
    Long_X: Number(v.longX),
    AssetType: v.assetType,
    AssetSubcat: v.assetSubcat,
    AssetDetail: v.assetDetail,
    Prj_Size: v.prjSize ?? undefined,
    OutputUnit: v.outputUnit || undefined,
    StartMonth: v.startMonth,
    StartYear: String(v.startYear),
    EndMonth: v.endMonth,
    EndYear: String(v.endYear),
    Villages: v.villages || undefined,
    Outcomes: v.outcomes,
    ImpactArea: v.impactArea || undefined,
    Extra_Info: v.extraInfo || undefined,
    AnalysisType: v.analysis,
  }
}
